import React, { FC, useState } from 'react';
import { NCFileVersionSelectorProps } from '@components/ncFileVersions/types';
import { getBoxShadowStyle } from '@styles/shadow';
import TouchableElement from '@components/common/touchableElement';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { scale } from '@components/frame';
import { SCREEN_BORDER_RADIUS, SCREEN_PADDING_VERTICAL_FOR_ITEM, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { BOX_SHADOW_TYPE, OTHER_VERSION_ELEMENT_HEIGHT } from '@components/ncFileVersions/constants';
import i18n from 'i18n-js';
import { font } from '@styles/fonts';
import Svg from '@styles/icons/svg';
import { isPhoneNative, isWeb } from '@helpers';
import getPcsCount from '@components/ncFileVersions/helpers/getPcsCount';
import getMachineNames from '@components/ncFileVersions/helpers/getMachineNames';
import formatDateRange from '@components/ncFileVersions/helpers/formatDateRange';

const NCFileVersionSelector: FC<NCFileVersionSelectorProps> = ({
  selected,
  C,
  version,
  isFavorite = false,
  onPress,
  onToggleFavorite,
  withFavoriteIcon,
  withShadow = false,
  roundBottom = true,
  roundTop = true,
  bottomLine = false,
  withSelectionIndicator = false,
}) => {
  const [isStarHovered, setIsStarHovered] = useState(false);
  const isPrimaryCard = !!withFavoriteIcon;
  const isHighlighted = selected && (!withSelectionIndicator || isPrimaryCard);
  const favoriteBlockPrimaryColor = C(isHighlighted ? 'Label/Light/AlwaysLight Primary' : 'Label/Light/Primary');
  const favoriteBlockSecondaryColor = C(isHighlighted ? 'Label/Light/AlwaysLight Secondary' : 'Label/Light/Tertiary');
  const selectedBg = 'Selected/Light/List item';
  const unselectedBg = 'Background/Light/Field';

  const shadowStyles = getBoxShadowStyle({ type: BOX_SHADOW_TYPE, C });
  const pcsCount = getPcsCount(version);
  const isVersionFavorite = typeof (version as any)?.isFavorite === 'boolean' ? (version as any)?.isFavorite : isFavorite;
  const inactiveLabel = i18n.t('Inactive');
  const showChevron = isPhoneNative;
  const machineNames = getMachineNames(version);
  const dateRange = formatDateRange(version);

  const selectionOffset = withSelectionIndicator ? scale(40) : scale(15);

  const cardTestID = typeof version?.ncFileVersionId === 'number' ? TEST_IDS.ncFileVersionSelector.card(version.ncFileVersionId) : undefined;
  // Render the "selected" sentinel whenever this card is selected — covers single-select
  // (highlighted favorite/other) and comparison-mode selection (checkbox chosen).
  const cardSelectedTestID = selected && typeof version?.ncFileVersionId === 'number' ? TEST_IDS.ncFileVersionSelector.cardSelected(version.ncFileVersionId) : undefined;
  const favoriteButtonTestID = typeof version?.ncFileVersionId === 'number' ? TEST_IDS.ncFileVersionSelector.favoriteButton(version.ncFileVersionId) : undefined;

  return (
    <TouchableElement testID={cardTestID} onPress={() => onPress(version)}>
      {({ pressHoverColor, boxStyle, hovered }) => {
        const showFavoriteIcon = isVersionFavorite || !isWeb || hovered;
        const favoriteIconColor = isVersionFavorite || isStarHovered ? favoriteBlockPrimaryColor : favoriteBlockSecondaryColor;

        return (
          <View
            style={[
              boxStyle,
              withShadow && shadowStyles,
              roundTop && {
                borderTopLeftRadius: scale(SCREEN_BORDER_RADIUS),
                borderTopRightRadius: scale(SCREEN_BORDER_RADIUS),
              },
              roundBottom && {
                borderBottomLeftRadius: scale(SCREEN_BORDER_RADIUS),
                borderBottomRightRadius: scale(SCREEN_BORDER_RADIUS),
              },
              {
                paddingLeft: selectionOffset,
                paddingRight: scale(16),
                paddingVertical: scale(SCREEN_PADDING_VERTICAL_FOR_ITEM),
                minHeight: scale(OTHER_VERSION_ELEMENT_HEIGHT),
                width: '100%',
                backgroundColor: isHighlighted ? C(selectedBg) : C(pressHoverColor(unselectedBg)),
              },
            ]}
          >
            {withSelectionIndicator && (
              <View
                style={{
                  position: 'absolute',
                  left: scale(15),
                  top: 0,
                  bottom: 0,
                  justifyContent: 'center',
                }}
              >
                <Svg
                  {...{
                    icon: selected ? 'selected' : 'unselected',
                    width: scale(20),
                    height: scale(20),
                    color: selected ? (isPrimaryCard ? 'Label/Light/AlwaysLight Primary' : 'Selected/Light/Primary') : 'Label/Light/Quinary',
                  }}
                />
              </View>
            )}
            <View>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', minHeight: scale(24) }}>
                <View style={{ flexDirection: 'row', gap: scale(8), alignItems: 'center' }}>
                  <Text style={[font.Headline, { color: favoriteBlockPrimaryColor }]}>
                    {i18n.t('Version')} {version.sequenceNumber}
                  </Text>
                  {version?.isActive === false && (
                    <View
                      style={{
                        paddingHorizontal: scale(8),
                        paddingVertical: scale(2),
                        borderRadius: scale(999),
                        borderWidth: StyleSheet.hairlineWidth,
                        borderColor: favoriteBlockSecondaryColor,
                      }}
                    >
                      <Text style={[font['Caption 2'], { color: favoriteBlockSecondaryColor }]}>{inactiveLabel}</Text>
                    </View>
                  )}
                  {showFavoriteIcon && (
                    <TouchableOpacity
                      testID={favoriteButtonTestID}
                      disabled={isVersionFavorite || !onToggleFavorite}
                      onPress={() => onToggleFavorite?.(version)}
                      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                      style={{ opacity: isVersionFavorite || onToggleFavorite ? 1 : 0.6 }}
                      {...((isWeb
                        ? {
                            onMouseEnter: () => setIsStarHovered(true),
                            onMouseLeave: () => setIsStarHovered(false),
                          }
                        : {}) as any)}
                    >
                      <Svg
                        {...{
                          icon: isVersionFavorite || isStarHovered ? 'starSelected' : 'starUnselected',
                          width: scale(18),
                          height: scale(18),
                          color: favoriteIconColor,
                        }}
                      />
                    </TouchableOpacity>
                  )}
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(4) }}>
                  {typeof pcsCount === 'number' && (
                    <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: scale(4) }}>
                      <Text style={[font['Time 2'], { color: favoriteBlockSecondaryColor }]}>{pcsCount}</Text>
                      <Text style={[font['Caption 2'], { color: favoriteBlockSecondaryColor }]}>{i18n.t('Pcs')}</Text>
                    </View>
                  )}
                  {showChevron && (
                    <Svg icon="chevronRight" width={scale(16)} height={scale(16)} color={isHighlighted ? 'Label/Light/AlwaysLight Secondary' : 'Label/Light/Tertiary'} />
                  )}
                </View>
              </View>
              <Text style={[font['Subhead 2'], { color: favoriteBlockSecondaryColor }]} numberOfLines={1}>{machineNames}</Text>
              <Text style={[font['Subhead 2'], { color: favoriteBlockSecondaryColor }]}>{dateRange}</Text>
            </View>
            {cardSelectedTestID && <View testID={cardSelectedTestID} />}
            {bottomLine && !selected && (
              <View
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: scale(15),
                  right: 0,
                  height: StyleSheet.hairlineWidth,
                  backgroundColor: C('Hairline/Light/No trans'),
                }}
              />
            )}
          </View>
        );
      }}
    </TouchableElement>
  );
};

export default NCFileVersionSelector;
